import React, { useState, useEffect } from 'react';
import { postRequest } from './Services/HttpClient';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import SanskritKeyboard from './SanskritKeyboard';
import RuleTraceDisplay from './RuleTraceDisplay';
import SwaraDharmaDisplay from './SwaraDharmaDisplay';
import './SandhiPage.css';

const STORAGE_KEY = 'sandhi_last_input';

const SandhiPage = () => {
  const [outputData, setOutputData] = useState('');
  const [inputData, setInputData] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return { inputWord: saved || '' };
  });
  const [showUdattaPrachaya, setShowUdattaPrachaya] = useState(true);
  const [apadaantam, setApadaantam] = useState(false);
  const [vedshaakha, setVedshaakha] = useState('तैत्तिरीय');
  const [includeRuleTrace, setIncludeRuleTrace] = useState(false);
  const [ruleTrace, setRuleTrace] = useState(null);
  const [includeSwaraDharma, setIncludeSwaraDharma] = useState(false);
  const [swaraDharmaData, setSwaraDharmaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showKeyboard, setShowKeyboard] = useState(true);
  // Input is pada-pāṭha: the server strips इति, drops daṇḍas and marks,
  // and joins each mantra up to the double daṇḍa. Needs a Vedic śākhā.
  const [padaPaatha, setPadaPaatha] = useState(false);
  const [padaPaathaInfo, setPadaPaathaInfo] = useState(null);
  // Save input to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, inputData.inputWord);
  }, [inputData.inputWord]);

  const handleChange = async () => {
    if (!inputData.inputWord || !inputData.inputWord.trim()) return;

    setOutputData('');
    setRuleTrace(null);
    setPadaPaathaInfo(null);
    setSwaraDharmaData(null);
    setError(null);
    setLoading(true);

    try {
      const requestBody = {
        input: inputData.inputWord,
        showUdattaPrachaya: showUdattaPrachaya,
        apadaantam: apadaantam
      };
      if (vedshaakha) {
        requestBody.vedshaakha = vedshaakha;
        if (padaPaatha) {
          requestBody.padaPaatha = true;
        }
      }
      if (includeRuleTrace) {
        requestBody.includeRuleTrace = true;
      }
      if (includeSwaraDharma) {
        requestBody.includeSwaraDharma = true;
      }
      const endpoint = 'v2/sandhi';
      const result = await postRequest(endpoint, JSON.stringify(requestBody));
      setOutputData(result.data || '');
      if (!result.data && result.error) {
        // Server explains plainly, e.g. a śākhā whose rules are not built yet
        setError(result.error);
      }
      if (result.ruleTrace) {
        setRuleTrace(result.ruleTrace);
      }
      if (result.swaraDharma) {
        setSwaraDharmaData(result.swaraDharma);
      }
      setPadaPaathaInfo(result.padaPaatha || null);
    } catch (err) {
      console.error('Sandhi API error:', err);
      setError('संधि-विश्लेषणे त्रुटिः। कृपया पुनः प्रयासं कुर्वन्तु। (Sandhi analysis failed. Please try again.)');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyboardKeyPress = (key) => {
    if (key === 'backspace') {
      setInputData(prev => ({
        ...prev,
        inputWord: prev.inputWord.slice(0, -1)
      }));
    } else {
      setInputData(prev => ({
        ...prev,
        inputWord: prev.inputWord + key
      }));
    }
  };

  const handleKeyboardEnter = () => {
    setInputData(prev => ({
      ...prev,
      inputWord: prev.inputWord + '\n'
    }));
  };

  const handleKeyboardClose = () => {
    setShowKeyboard(false);
  };

  const clear = () => {
    setInputData({ inputWord: '' });
    setOutputData('');
    setRuleTrace(null);
    setPadaPaathaInfo(null);
    setSwaraDharmaData(null);
    setError(null);
  };

  const handleIncludeRuleTraceChange = (evt) => {
    setIncludeRuleTrace(evt.target.checked);
  };

  const updateInputStateInputWord = (inputWordEvent) => {
    const inputWord = inputWordEvent.target.value;
    setInputData({ ...inputData, inputWord });
  };

  const handleUdattaPrachayaChange = (evt) => {
    setShowUdattaPrachaya(evt.target.checked);
  };

  const handleApadaantamChange = (evt) => {
    setApadaantam(evt.target.checked);
  };

  const handleVedshaakhaChange = (evt) => {
    setVedshaakha(evt.target.value);
    if (!evt.target.value) {
      setPadaPaatha(false);
    }
  };

  return (
    <Container fluid="md" className="sandhi-page">
      {/* Header with title */}
      <Row>
        <div className="sandhi-header">
          <h1 className="sandhi-title">संधिः (Sandhi)</h1>
        </div>
      </Row>

      {/* Options Bar */}
      <Row className="sandhi-options-row">
        <div className="sandhi-options">
          <div className="sandhi-shakha-group">
            <label className="sandhi-label">
              <span className="label-text">वेदशाखा:</span>
              <select
                value={vedshaakha}
                onChange={handleVedshaakhaChange}
                className="sandhi-select"
              >
                <option value="">--</option>
                <option value="तैत्तिरीय">तैत्तिरीय</option>
                <option value="शाकल">शाकल</option>
                <option value="शौनक">शौनक</option>
                <option value="काण्व">काण्व</option>
                <option value="कौथुम">कौथुम</option>
                <option value="माध्यन्दिन">माध्यन्दिन</option>
              </select>
            </label>
          </div>
          <div className="sandhi-checkboxes">
            <label className="sandhi-checkbox">
              <input
                type="checkbox"
                checked={apadaantam}
                onChange={handleApadaantamChange}
              />
              <span>अपदान्तम्</span>
            </label>
            <label
              className="sandhi-checkbox"
              title={vedshaakha
                ? 'Input is pada-pāṭha: इति is stripped, daṇḍas and marks removed, sandhi runs to each double daṇḍa'
                : 'Choose a वेदशाखा first — pada-pāṭha is read in its Vedic context'}
            >
              <input
                type="checkbox"
                checked={padaPaatha}
                disabled={!vedshaakha}
                onChange={(evt) => setPadaPaatha(evt.target.checked)}
              />
              <span>पदपाठः</span>
            </label>
            <label className="sandhi-checkbox">
              <input
                type="checkbox"
                checked={showUdattaPrachaya}
                onChange={handleUdattaPrachayaChange}
              />
              <span>उदात्त-प्रचय</span>
            </label>
            <label className="sandhi-checkbox">
              <input
                type="checkbox"
                checked={includeRuleTrace}
                onChange={handleIncludeRuleTraceChange}
              />
              <span>नियम-दर्शनम्</span>
            </label>
            <label className="sandhi-checkbox">
              <input
                type="checkbox"
                checked={includeSwaraDharma}
                onChange={(evt) => setIncludeSwaraDharma(evt.target.checked)}
              />
              <span>स्वरधर्मः</span>
            </label>
          </div>
        </div>
      </Row>

      {/* Main Content - Side by side on tablet+, stacked on mobile */}
      <Row className="sandhi-main-row">
        {/* Input Column */}
        <Col xs={12} md={6} className="sandhi-col sandhi-input-col">
          <div className="sandhi-section">
            <div className="sandhi-section-header">
              <span className="section-label">निवेशः (Input)</span>
            </div>
            <textarea
              className="sandhi-textarea sandhi-input"
              onFocus={() => setShowKeyboard(true)}
              name="textarea"
              value={inputData.inputWord}
              disabled={loading}
              onChange={updateInputStateInputWord}
              placeholder="संस्कृत-पाठं लिखतु...&#10;(Enter Sanskrit text for sandhi)"
            />
          </div>
        </Col>

        {/* Output Column */}
        <Col xs={12} md={6} className="sandhi-col sandhi-output-col">
          <div className="sandhi-section">
            <div className="sandhi-section-header">
              <span className="section-label">फलम्</span>
            </div>
            <textarea
              className="sandhi-textarea sandhi-output"
              name="textarea"
              value={loading ? 'विश्लेषणम्...' : outputData}
              readOnly
              placeholder="फलं अत्र प्रदर्श्यते...&#10;(Output will appear here)"
            />
          </div>
        </Col>
      </Row>

      {/* Action Buttons */}
      <Row className="sandhi-actions-row">
        <div className="sandhi-actions">
          <Button
            variant="primary"
            onClick={handleChange}
            disabled={loading || !inputData.inputWord || !inputData.inputWord.trim()}
            className="sandhi-submit-btn"
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                विश्लेषणम्...
              </>
            ) : (
              'संधिं कुरु (Apply)'
            )}
          </Button>
          <Button
            variant="outline-secondary"
            onClick={clear}
            className="sandhi-clear-btn"
          >
            स्वच्छं कुरु (Clear)
          </Button>
        </div>
      </Row>

      {/* Error Display */}
      {error && (
        <Row className="sandhi-error-row">
          <div className="sandhi-error">
            {error}
          </div>
        </Row>
      )}

      {/* Pada-pāṭha step: what was converted before sandhi */}
      {padaPaathaInfo && (
        <Row className="sandhi-error-row">
          <div className="sandhi-padapaatha-info" style={{ fontSize: '0.9em', opacity: 0.85 }}>
            पदपाठः — {padaPaathaInfo.changedPadas.length} pada(s) converted to prayoga form before sandhi
            {padaPaathaInfo.changedPadas.length > 0 && (
              <span>: {padaPaathaInfo.changedPadas.slice(0, 8).map(c => `${c.pada} → ${c.prayoga}`).join(' · ')}
                {padaPaathaInfo.changedPadas.length > 8 ? ' …' : ''}</span>
            )}
          </div>
        </Row>
      )}

      {/* Rule Trace Display */}
      {includeRuleTrace && ruleTrace && (
        <Row className="sandhi-trace-row">
          <RuleTraceDisplay ruleTrace={ruleTrace} outputText={outputData} />
        </Row>
      )}

      {/* Swara Dharma Display */}
      {includeSwaraDharma && swaraDharmaData && (
        <Row className="sandhi-swara-row">
          <SwaraDharmaDisplay
            inputSwaraDharma={swaraDharmaData.input}
            outputSwaraDharma={swaraDharmaData.output}
            outputIsNested={false}
          />
        </Row>
      )}

      {/* Sanskrit Keyboard */}
      <Row className="sandhi-keyboard-row">
        <SanskritKeyboard
          visible={showKeyboard}
          onKeyPress={handleKeyboardKeyPress}
          onEnter={handleKeyboardEnter}
          onClose={handleKeyboardClose}
        />
      </Row>
    </Container>
  );
};

export default SandhiPage;
